/**
 * Dateityp-Erkennung für die Vorschau — eine Regel, eine Quelle.
 *
 * Die Helfer lagen früher verstreut (`isPdfLike`/`isTextLike` in
 * `DocumentViewerContent`, `PREVIEW_IMG_EXT` in `DocumentsSection`), obwohl sie
 * dieselbe Frage beantworten: „kann der Browser das inline darstellen?".
 * Seit dem Viewer-Rollout (PO-Notiz 26.07.2026, Punkt 12) stellen Vorgang,
 * Todo, Kundenakte und Mail-Anhänge dieselbe Frage — deshalb hier gebündelt.
 *
 * Bewusst DOM-frei, damit der (node-only) Vitest-Runner sie prüfen kann.
 */

const extOf = (fileName: string): string => {
    const i = fileName.lastIndexOf(".");
    return i >= 0 && i < fileName.length - 1 ? fileName.slice(i + 1).toLowerCase() : "";
};

export const isPdfLike = (fileName: string, mimeType?: string | null): boolean =>
    (mimeType ?? "").toLowerCase().includes("pdf") || extOf(fileName) === "pdf";

export const isTextLike = (fileName: string, mimeType?: string | null): boolean =>
    (mimeType ?? "").startsWith("text/") || ["txt", "csv", "log", "md"].includes(extOf(fileName));

/**
 * Browser-darstellbare Bildformate. TIFF ist bewusst ausgenommen: der MIME-Typ
 * sagt `image/tiff`, aber kein gängiger Browser rendert es — ein `<img>` bliebe
 * leer.
 */
const PREVIEW_IMG_EXT = new Set(["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"]);

/**
 * Bildformate, die zwar `image/…` heißen, die der Browser aber nicht als `<img>`
 * dekodiert (HEIC/HEIF vom iPhone, TIFF aus dem Scanner) — sie gehören in den
 * Download, nicht in eine leere Bildfläche.
 */
const NICHT_DARSTELLBAR_IMG = ["tiff", "tif", "heic", "heif"];

export const isPreviewImage = (fileName: string, mimeType?: string | null): boolean => {
    const m = (mimeType ?? "").toLowerCase().split(";")[0].trim();
    if (NICHT_DARSTELLBAR_IMG.includes(extOf(fileName))) return false;
    if (m.startsWith("image/")) return !NICHT_DARSTELLBAR_IMG.some((t) => m.includes(t));
    return PREVIEW_IMG_EXT.has(extOf(fileName));
};

/**
 * Bilder, die sich im PDF-Editor öffnen und bearbeiten lassen (User 04.10.2026).
 *
 * Enger als {@link isPreviewImage}: SVG ist kein Pixelbild (und könnte Skripte
 * tragen), HEIC/TIFF kann der Browser nicht dekodieren. SPIEGELT
 * `istBearbeitbaresBild` in `apps/api/src/document-editing/bild-format.util.ts`
 * — die Serverseite bleibt maßgeblich.
 */
const EDIT_IMG_EXT = new Set(["jpg", "jpeg", "png", "webp", "gif", "bmp"]);
const EDIT_IMG_MIME = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif", "image/bmp"]);

export const isEditableImage = (fileName: string, mimeType?: string | null): boolean => {
    if (isPdfLike(fileName, mimeType)) return false;
    const m = (mimeType ?? "").toLowerCase().split(";")[0].trim();
    return EDIT_IMG_MIME.has(m) || EDIT_IMG_EXT.has(extOf(fileName));
};

/**
 * Archivierte Mail-Datei (`.msg` aus Outlook, `.eml` nach RFC 822).
 *
 * Der MIME-Typ ist hier unzuverlässig — Uploads tragen je nach Quelle
 * `application/octet-stream`, `application/vnd.ms-outlook` oder
 * `message/rfc822`. Maßgeblich ist die Endung.
 */
export const isMailFileLike = (fileName: string, mimeType?: string | null): boolean =>
    ["msg", "eml"].includes(extOf(fileName)) ||
    (mimeType ?? "").toLowerCase() === "message/rfc822";

/**
 * Office-Dateien, die der ONLYOFFICE-Editor öffnen kann.
 *
 * SPIEGELT `apps/api/src/office-editing/office-format.util.ts`. Die Liste steht
 * hier ein zweites Mal, weil sie zwei verschiedene Fragen beantwortet: dort
 * „darf gespeichert werden?" (Sicherheitsschranke), hier „lohnt sich der
 * Editor-Aufruf?" (Wegweiser). Wer eine Endung ergänzt, ergänzt sie in BEIDEN
 * Dateien — die Backend-Seite bleibt maßgeblich und weist ab, was sie nicht
 * kennt.
 *
 * Bewusst inklusive der Altformate (.doc/.xls/.odt): der Editor ZEIGT sie, er
 * schreibt sie nur nicht zurück. Ein Download wäre hier die schlechtere
 * Antwort — ansehen geht, und darum geht es beim Klick.
 */
const OFFICE_EXT = new Set([
    "docx", "doc", "odt", "rtf",
    "xlsx", "xlsm", "xls", "ods",
    "pptx", "ppt", "odp",
]);

/**
 * `.csv` und `.txt` fehlen bewusst, obwohl der Document Server sie öffnen
 * könnte: `isTextLike` bedient sie bereits, und zurückschreiben darf der Editor
 * sie ohnehin nicht (er legte Zeichensatz und Trennzeichen nach eigenem
 * Ermessen fest). Beide Wege böten also nur Ansehen — dann gewinnt der, der
 * schon da ist und keinen 1,4-GB-Dienst braucht.
 *
 * Das Backend kennt `.csv` weiterhin: es ist die tolerantere Seite und würde
 * einen gezielten Aufruf bedienen. Der Wegweiser hier schickt nur niemanden hin.
 */

export const isOfficeLike = (fileName: string, mimeType?: string | null): boolean => {
    // Der MIME-Typ ist bei Office-Uploads unzuverlässig (je nach Quelle
    // `application/octet-stream`), aber wenn er eindeutig ist, glauben wir ihm.
    const m = (mimeType ?? "").toLowerCase();
    if (m.includes("officedocument") || m.includes("opendocument") || m.includes("ms-excel") || m.includes("msword")) {
        return true;
    }
    return OFFICE_EXT.has(extOf(fileName));
};

/**
 * Leitfrage jedes Klicks auf eine Datei: Viewer öffnen oder herunterladen?
 * Alles, was hier `false` liefert (docx, xlsx, zip, tiff …), gehört in den
 * Download — der Viewer würde sonst eine leere Fläche zeigen.
 *
 * ACHTUNG — beantwortet nur: „kann der BROWSER diesen Blob darstellen?".
 * Mail-Dateien gehören bewusst NICHT dazu: sie werden serverseitig geparst und
 * brauchen eine Dokument-Id. Wer eine solche hat, fragt
 * {@link canOpenDocumentInViewer}; wer nur einen Blob hat (z. B. Mail-Anhänge im
 * Graph-Fenster), bleibt bei dieser Funktion.
 */
export const canPreviewInline = (fileName: string, mimeType?: string | null): boolean =>
    ["pdf", "bild", "text"].includes(oeffnenArt(fileName, mimeType));

/**
 * Wie {@link canPreviewInline}, zusätzlich mit den serverseitig aufbereiteten
 * Typen. Gilt für alles, was als KMVS-Dokument mit Id vorliegt.
 */
export const canOpenDocumentInViewer = (fileName: string, mimeType?: string | null): boolean =>
    ["mail", "pdf", "bild-editor", "bild", "text"].includes(oeffnenArt(fileName, mimeType, { mitDokumentId: true }));

/**
 * DIE Weiche beim Öffnen einer Datei (22.10.2026: Bilder landeten im
 * PDF-Viewer und zeigten „Failed to load PDF file“). Jeder Öffnen-Weg —
 * Vorgang, To-do, Kundenakte, Mail-Anhänge, Bilder aus dem Mailtext, Explorer,
 * Hover-Vorschau — fragt hier und nirgends sonst:
 *
 *  - `mail`        — `.msg`/`.eml`, serverseitig geparst (braucht Dokument-Id);
 *  - `pdf`         — PDF-Viewer (pdf.js);
 *  - `bild-editor` — Pixelbild eines KMVS-Dokuments: Ansicht/Editor mit
 *                    „Bearbeiten“ (User 04.10.2026). Ob es WIRKLICH ein Bild
 *                    ist, prüft die Ansicht danach an den Bytes;
 *  - `bild`        — Bildvorschau mit Zoom (ohne Dokument-Id, oder SVG);
 *  - `text`        — `<pre>`;
 *  - `office`      — Office-Editor/Zielwahl (braucht Dokument-Id);
 *  - `download`    — alles andere (zip, tiff, heic, unbekannt …).
 *
 * Endung zählt unabhängig von der Schreibweise (`.JPG`), ein fehlender oder
 * nichtssagender MIME-Typ (`application/octet-stream`) fällt auf die Endung
 * zurück. Ein Bild geht NIE in den PDF-Viewer — außer die Datei heißt `.pdf`
 * oder trägt `application/pdf`.
 */
export type OeffnenArt = "mail" | "pdf" | "bild-editor" | "bild" | "text" | "office" | "download";

export function oeffnenArt(
    fileName: string,
    mimeType?: string | null,
    opts: { mitDokumentId?: boolean } = {},
): OeffnenArt {
    const mitId = !!opts.mitDokumentId;
    if (mitId && isMailFileLike(fileName, mimeType)) return "mail";
    if (isPdfLike(fileName, mimeType)) return "pdf";
    if (isEditableImage(fileName, mimeType)) {
        // Bearbeiten braucht die Dokument-Id; zeigen kann der Browser es nur,
        // wenn es kein HEIC/TIFF ist, das unter einem Bild-MIME daherkommt.
        if (mitId) return "bild-editor";
        if (isPreviewImage(fileName, mimeType)) return "bild";
    }
    if (isPreviewImage(fileName, mimeType)) return "bild";
    if (isTextLike(fileName, mimeType)) return "text";
    if (mitId && isOfficeLike(fileName, mimeType)) return "office";
    return "download";
}
